import { InjectionToken, Injectable, EventEmitter, Injector, Type } from '@angular/core';
import { AppTextService } from '@fi-shared/app-text';
import { IFiHttpPromise } from '@fi-shared/to-fi-promise';
import { ILightWeightObjectCollection, ILocalSearchDataItemModel, LocalSearchModel } from '@fi-shared/models';
import { ItemsListHeader } from './items-list-header';
import { ListViewSettingModel } from './list-view-setting.model';
import { BehaviorSubject, Observable, ReplaySubject, Subscription } from 'rxjs';
import { SideListOptionType } from '@fi-shared/types/side-list-option-type';
import { FiImageSize } from '@fi-shared/types/image-size';
import { UserPreferencesService } from '@fi-shared/fi-shared-services/user-preferences.service';
import { LocalSearchStorageService } from '@fi-app/utilities/fi-services/local-search-storage.service';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { propComparator } from '@fi-shared/utils/array-sort-comparators';
import { isDefined } from '@fi-shared/utils/is-defined';
import { ItemsListContentBaseComponent } from './list-content/content-base/content-base.component';
import { ItemsListElementType, ItemsListHeaderProperties } from '@fi-app/components/items-list/items-list-header';
import { FiDateFormatOptions } from '@fi-shared/utils/format-date';
import { Subject } from 'rxjs';
import { FiMediaStorageService } from '@fi-shared/media-bank/fi-media-storage.service';
import { FiSnackBarService } from '@fi-shared/fi-snackbar/snack-bar.service';
import { isNullOrUndefined } from 'util';
import { takeUntil } from 'rxjs/operators';
import { FiDisplayType } from '@fi-shared/types/display-type';
import { IItemsListAction2, IListViewTag, ISideListOptionsItem } from '@fi-shared/fi-items-list/fi-items-list';
import { FiAppSettings } from '@fi-shared/fi-app-settings';
import { UserService } from '@fi-app/user/user.service';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { ILocalSearchParameter, IPublicationItemBase } from '@fi-shared/models/models';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';

export let LIST_DATA = new InjectionToken<ListData<any>>('ccmListData');
export const LIST_CONTAINER_DATA = new InjectionToken<any>('ListContainerData');

@Injectable()
export class ListDataService {
    private listItemSelectedChange = new Subject<any[]>();

    constructor() {}

    onListItemSelectedChange(callback: (selectedItems: any[]) => void): Subscription {
        return this.listItemSelectedChange.subscribe(callback);
    }

    raiseListItemSelectedChange(selectedItems: any[]) {
        this.listItemSelectedChange.next(selectedItems);
    }
}

interface IStateForFilter {
    selectedTags?: string;
    offset?: number;
    sortColumn?: string;
    sortDirection?: boolean;
}

export class ListData<T> {
    Header: ItemsListHeader[] = [];
    Data: T[] = [];
    SelectedItems: T[] = []; // don't forget emit selectedItemsChange$ on every changing of selected items collection
    TotalDataCount = 0;
    TogglePropertyName = 'Checked';
    MultiSelect = true;
    CanShowLoadMore = true;
    RowIsClickable = true;

    LoadingMore$ = new BehaviorSubject<boolean>(false);
    ngOnDestroy$ = new Subject<void>();

    ChildItems = [];

    ShowFavoriteIcon = false;
    ShowCheckBox: any = true;
    ShowCheckAll = true;
    ShowDelete = true;
    IsExtraButtonsShown = true;
    ShowDeleteMobile = true;
    DeleteButtonText;
    NameColumnIndex = 1;
    GridSecondColumnIndex = 2;
    ChildrenInitiallyCollased = true;
    AllowUncheckedParentWithCheckedChildren = false;
    InitialParentData = undefined;

    Buttons: IItemsListAction2[] = [];

    DisplayType: FiDisplayType = FiDisplayType.List;
    ObjectType = null;

    Sort = {
        //todo make private
        Column: 'Name',
        Reverse: false,
    };

    IdColumn = 'Id';
    SortByNameColumn: any = 'Name';
    SortByDateColumn = 'ChangedOn';

    Statefull = true;
    MobileMoreOpened = false;
    SideMenuOpen$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    FilterType = SideListOptionType.SharedWithMe;

    DeletePermanentlyTextId: string = null;
    DeleteConfirmMessage: string;
    DeleteMultipleConfirmMessage: string = undefined;
    deleteConfirm?: (deleteConfirmMessage: string, selectedIds?: number[], selectedItems?: any[]) => Promise<T[]>;

    restoreFunc: (itemsToRestore: T[]) => Promise<any>;

    ParentProperty = null;
    ChildProperty: string = null;
    isWithChildren: () => boolean;
    ChildCountProperty: string;
    collapsedParents = [];

    BorderBottom = true;
    ListItemBigPadding = false;
    baseLoadParameters: { [prop: string]: any };

    //itemsMap = {};
    parentChildMap = {};
    // override if parent and children entities have different types and can have same Id's
    protected getKeyForParentChildMapping(item) {
        return item[this.IdColumn];
    }

    // IconClass: string | ((item: T)=> string) = 'icon-objectCcmIcon';
    IconClass: any = 'icon-objectCcmIcon';

    GridItemClick = undefined;

    selectedTags: number[] = [];
    isTagSelected(tag: IListViewTag) {
        return this.selectedTags && this.selectedTags.indexOf(tag.Id) !== -1;
    }

    CcmListItemNameClick = undefined;

    showDisplayTypeSwitch = true; // todo: remove, not used

    moreMenuMobileItems = null;

    listRowAfterComponentType: any;

    loadDataFunc: () => Promise<void> = null;
    loadCountFunc: () => IFiHttpPromise<number> = null;
    localSearchFunc: () => Promise<void> = null;
    deleteFunc: (selectedIds: number[], selectedItems: any[]) => IFiHttpPromise<any>;
    SearchFunction: () => void;
    GetItemName: (isPlural?: boolean) => string;
    ListViewSettings: ListViewSettingModel;
    ShowHeadersOnMobile = false;
    SelectTag: (tag: IListViewTag, skipUpdatingSelectedTags?: boolean) => void;
    toggleChildrenPlaceholderAfterComponent?: Type<any>;
    listContentComponent?: Type<ItemsListContentBaseComponent>;

    private _initialOffset = null;

    private _loadCountInternalFunc: (onlyCancel?: boolean, skipTrackPromise?: boolean) => IFiHttpPromise<any>;
    private _loadDataInternalFunc: (onlyCancel?: boolean, skipTrackPromise?: boolean) => IFiHttpPromise<any>;
    private _localSearchInternalFunc: (onlyCancel?: boolean, skipTrackPromise?: boolean) => IFiHttpPromise<any>;

    _displayTypeChanged$ = new ReplaySubject<void>(1);
    get displayTypeChanged$() {
        return this._displayTypeChanged$.asObservable();
    }
    raiseDisplayTypeChanged() {
        this._displayTypeChanged$.next();
    }

    private _displayTypeLoaded$ = new ReplaySubject<void>(1);
    get displayTypeLoaded$() {
        return this._displayTypeLoaded$.asObservable();
    }
    raiseDisplayTypeLoaded() {
        this._displayTypeLoaded$.next();
    }

    private _sortChanged$ = new ReplaySubject<void>(1);
    get sortChanged$() {
        return this._sortChanged$.asObservable();
    }
    raiseSortChanged() {
        this._sortChanged$.next();
    }

    private _selectedItems$ = new ReplaySubject<T[]>(1);
    get selectedItems$() {
        return this._selectedItems$.asObservable();
    }
    raiseSelectedItemsChanged() {
        this._selectedItems$.next([...this.SelectedItems]);
    }

    private _localSearchTextChanged$ = new ReplaySubject<string>(1);
    get localSearchTextChanged$() {
        return this._localSearchTextChanged$.asObservable();
    }
    raiseLocalSearchTextChanged($event: string) {
        this._localSearchTextChanged$.next($event);
    }

    onAppendData = new EventEmitter<number>();
    favoriteChanged$ = new Subject<void>();
    showMoreChildren$ = new Subject<T>(); // invoked when user clicks 'Show more ' link in item, which has children
    dataCleared$ = new Subject<void>();
    onDeleteClick = new EventEmitter();
    onInitialized = new EventEmitter<number>();

    private userPreferencesService: UserPreferencesService;
    private localSearchStorageService: LocalSearchStorageService;
    protected stateService: FiStateService;
    protected appTextService: AppTextService;
    protected snackBar: FiSnackBarService;
    protected userService: UserService;
    protected userDataService: FiUserDataService;
    protected fiFormatDateService: FiFormatDateService;
    fetchingAllData: boolean;

    constructor(
        protected injector: Injector,
        private isLocalSort?: boolean,
        defaultColumn?: string,
        defaultReverse?: boolean,
        sortNullValuesAtTheEnd?: boolean,
    ) {
        if (isNullOrUndefined(sortNullValuesAtTheEnd)) sortNullValuesAtTheEnd = true;

        this.userPreferencesService = injector.get(UserPreferencesService);
        this.localSearchStorageService = injector.get(LocalSearchStorageService);
        this.stateService = injector.get(FiStateService);
        this.appTextService = injector.get(AppTextService);
        this.snackBar = injector.get(FiSnackBarService);
        this.userService = injector.get(UserService);
        this.userDataService = injector.get(FiUserDataService);
        this.fiFormatDateService = injector.get(FiFormatDateService);
        this.DeleteConfirmMessage = this.appTextService.get('delete.confirm.items');

        if (isDefined(defaultColumn)) this.Sort.Column = defaultColumn;

        if (isDefined(defaultReverse)) this.Sort.Reverse = defaultReverse;

        let oldSort = Object.assign({}, this.Sort);
        this.sortChanged$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
            if (isLocalSort) {
                const newSort = this.Sort;
                if (newSort !== oldSort) {
                    const newData = this.Data.sort(
                        propComparator(newSort.Column, newSort.Reverse, sortNullValuesAtTheEnd),
                    ).slice(0);
                    this.clearData();
                    this.appendData(newData);
                    oldSort = Object.assign({}, this.Sort);
                }
            } else {
                this.clearData();
                this.LoadingMore$.next(true);

                this.fetchingAllData = false;
                this._loadDataInternalFunc().finally(() => this.LoadingMore$.next(false));
            }

            this.userPreferencesService.saveListSort(
                this.getKeyForListSort(),
                Object.assign({
                    Column: this.Sort.Column,
                    Reverse: this.Sort.Reverse,
                }),
            );
        });

        this._loadDataInternalFunc = internalLoadFuncFactory(() => {
            return this.loadDataFunc;
        });

        this._loadCountInternalFunc = internalLoadFuncFactory(() => {
            return this.loadCountFunc;
        });

        this._localSearchInternalFunc = internalLoadFuncFactory(() => {
            return this.localSearchFunc;
        });

        this.DeleteButtonText = this.appTextService.get('common.delete');

        function internalLoadFuncFactory(loadFuncAccessor) {
            let loadFuncPromise = null;
            return function (onlyCancel) {
                const loadFunc = loadFuncAccessor();
                if (loadFunc) {
                    if (loadFuncPromise && loadFuncPromise.cancel) {
                        loadFuncPromise.cancel();
                        loadFuncPromise = null;
                    }
                    if (!onlyCancel) {
                        loadFuncPromise = loadFunc();
                    }
                    return loadFuncPromise;
                }
            };
        }

        this.raiseSelectedItemsChanged();
        this.selectedItems$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
            this.injector.get(ListDataService).raiseListItemSelectedChange(this.SelectedItems);
        });
    }

    protected getKeyForListSort(): FiItemsListType {
        return FiItemsListType.Unknown; //todo
    }

    Initialize(initialSearchObjects?: ILocalSearchDataItemModel[]) {
        this.loadSortOrderFromState();

        if (this.isV2SearchEnabled) {
            /*if (initialSearchObjects) {
                initialSearchObjects.forEach(searchObject => {
                    this.isV2SearchEnabled.AddedObjects.push(searchObject);
                });
            } else {
                // todo is this part still working? probably better use getKeyForListSort() instead of [Module]
                const localSearch = this.localSearchStorageService.GetLastSearchParameters();
                // apply previous search parameters
                if (localSearch.SearchParams && localSearch.SearchParams.length > 0) {
                    localSearch.SearchParams.forEach((param) => {
                        this.isV2SearchEnabled.AddedObjects.push(param);
                    });
                }
            }*/
        }

        if (this.localSearchFunc && !this.SearchFunction) {
            // add search function only if we have localSearchFunc defined
            this.SearchFunction = () => this.reload();
        }

        this.initializeInternal().then(() => {
            // loading finished
            this.onInitialized.next(this._initialOffset || 0);
            this._initialOffset = null;
        });
    }

    private reload() {
        this.CancelRequests();
        this.clearData();
        this.initializeInternal();
    }

    private initializeInternal() {
        // load and set total count, load data and local search data
        this.LoadingMore$.next(true);
        const loadCountInternalPromise = this._loadCountInternalFunc();
        if (loadCountInternalPromise) {
            loadCountInternalPromise.then((count) => {
                this.TotalDataCount = count.data ? count.data : count;
            });
        }

        this.fetchingAllData = true;
        let initPromise = this._loadDataInternalFunc();

        if (this.localSearchFunc) initPromise = initPromise.then(() => this._localSearchInternalFunc());

        initPromise.then(() => this.LoadingMore$.next(false));
        return initPromise;
    }

    CancelRequests() {
        this._loadDataInternalFunc(true);
        this._loadCountInternalFunc(true);
        this._localSearchInternalFunc(true);
    }

    private showMoreCount = 0;
    showMoreDone = new BehaviorSubject(this.showMoreCount);

    //This is the function for showmore scrolling.
    ShowMore() {
        const promise = this.ShowMorePromise();
        promise.finally(() => this.showMoreDone.next(++this.showMoreCount));
        return promise;
    }

    ShowMorePromise() {
        const promise = new Promise<void>((resolve, reject) => {
            if (!this.loadDataFunc || this._initialOffset /*first loading not finished*/) {
                setTimeout(() => reject());
            } else {
                if (this.isLoadMoreAvailable()) {
                    this.LoadingMore$.next(true);

                    this.fetchingAllData = false;
                    const promise = this._loadDataInternalFunc(undefined, true /* dont show main loader */);

                    promise
                        .then(
                            () => resolve(),
                            () => reject(),
                        )
                        .finally(() => this.LoadingMore$.next(false));
                } else {
                    setTimeout(() => reject());
                }
            }
        });

        return promise;
    }

    GetCheckedItems() {
        return this.SelectedItems;
    }

    IsShowLoadMore() {
        return this.CanShowLoadMore && this.Data.length > 0 && this.Data.length < this.TotalDataCount;
    }

    private loadSortOrderForLocalList() {
        if (this.isLocalSort) {
            const listSort: { Column: string; Reverse: boolean } = this.userPreferencesService.getListSort(
                this.getKeyForListSort(),
            );
            if (listSort) {
                this.Sort.Column = listSort.Column;
                this.Sort.Reverse = listSort.Reverse;
            }
        }
    }
    // https://chainformation.atlassian.net/browse/FI-3066
    private loadSortOrderFromState() {
        if (!this.isLocalSort) {
            const listSort: { Column: string; Reverse: boolean } =
                this.Statefull && this.stateService.params.sortColumn
                    ? {
                          Column: this.stateService.params.sortColumn,
                          Reverse: this.stateService.params.sortDirection === 'true',
                      }
                    : this.userPreferencesService.getListSort(this.getKeyForListSort());

            if (listSort && this.Header.find((item) => item.PropertyName == listSort.Column)) {
                this.Sort.Column = listSort.Column;
                this.Sort.Reverse = listSort.Reverse;
            }

            this.loadOffsetFromState();
        }
    }

    private loadOffsetFromState() {
        if (this.Statefull && this.stateService.params.hasOwnProperty('offset') && this.stateService.params.offset)
            this._initialOffset = parseInt(this.stateService.params.offset);
    }

    SetTotalItemsCount(count: number) {
        //this.TotalDataCount = count;
    }
    setTotalItemsCountNew(count: number) {
        this.TotalDataCount = count;
    }

    RemoveFromList(removeObjects, failedIds?: any[]) {
        const _this = this,
            removeObjectsCopy = [].concat(removeObjects),
            itemsCount = this.Data.length;

        if (!isDefined(failedIds)) failedIds = [];

        function removeItemFromChildTree(object, dataSet: any[]) {
            const index = dataSet.map((item) => item[_this.IdColumn]).indexOf(object[_this.IdColumn]);
            if (index > -1) {
                dataSet.splice(index, 1);
                const selectedIdx = _this.SelectedItems.map((item) => item[_this.IdColumn]).indexOf(
                    object[_this.IdColumn],
                );
                if (selectedIdx > -1) _this.SelectedItems.splice(selectedIdx, 1);

                return true;
            }
            if (_this.ChildProperty) {
                for (let i = 0; i < dataSet.length; i++) {
                    if (
                        dataSet[i][_this.ChildProperty] &&
                        removeItemFromChildTree(object, dataSet[i][_this.ChildProperty])
                    )
                        return true;
                }
                return false;
            }
            return false;
        }

        removeObjectsCopy.forEach(function (object) {
            if (failedIds.indexOf(object.Id) === -1) {
                object.Checked = false;
                removeItemFromChildTree(object, _this.Data);
            }
        });

        this.SelectedItems.forEach((selectedItem) => {
            if (failedIds.indexOf(selectedItem[_this.IdColumn]) === -1) {
                this.SelectedItems.splice(this.SelectedItems.indexOf(selectedItem), 1);
            }
        });

        this.TotalDataCount -= itemsCount - this.Data.length;
        this.raiseSelectedItemsChanged();
    }

    ToggleMobileMore($event?) {
        if ($event) $event.preventDefault();
        this.MobileMoreOpened = !this.MobileMoreOpened;
    }

    ToggleSideMenu() {
        this.SideMenuOpen$.next(!this.SideMenuOpen$.value);
    }

    setFilterType(filterType) {
        this.FilterType = filterType;
    }

    setFromStateFilterType(sideListOptions?: ISideListOptionsItem[]) {
        if (isDefined(this.stateService.params.filterType)) {
            let filterType = this.stateService.params.filterType
                ? parseInt(this.stateService.params.filterType)
                : SideListOptionType.SharedWithMe;
            const canSetFilterType =
                !sideListOptions || sideListOptions.filter((item) => item.value == filterType).length > 0;
            if (!canSetFilterType) filterType = SideListOptionType.SharedWithMe;

            this.setFilterType(filterType);
        }
    }

    setFromStateSelectedTags() {
        if (this.stateService.params.selectedTags)
            this.selectedTags = this.stateService.params.selectedTags.split('|').map((val) => parseInt(val));
    }

    private sortOrderWasLoadedForLocalList = false;
    appendData(dataSet: T[], addToStart = false) {
        if (!this.sortOrderWasLoadedForLocalList) {
            this.loadSortOrderForLocalList();
            this.sortOrderWasLoadedForLocalList = true;
        }

        const newData = this.isLocalSort
            ? dataSet.sort(propComparator(this.Sort.Column, this.Sort.Reverse, true))
            : dataSet;
        for (let i = 0; i < newData.length; i++) {
            const item = newData[i];
            //this.itemsMap[item[this.IdColumn]] = item;
            if (!addToStart) {
                this.Data.push(item);
            } else {
                this.Data.unshift(item);
            }
            mapParentChild.call(this, item);
        }

        function mapParentChild(item) {
            const self = this;

            if (self.ChildProperty) {
                const children = item[self.ChildProperty];
                if (children) {
                    children.forEach((child) => {
                        if (!self.parentChildMap.hasOwnProperty(self.getKeyForParentChildMapping(child)))
                            self.parentChildMap[self.getKeyForParentChildMapping(child)] = item;

                        //self.itemsMap[child[self.IdColumn]] = child;
                        mapParentChild.call(self, child);
                    });
                }
            }
        }

        this.onAppendData.emit(dataSet.length);
        // https://chainformation.atlassian.net/browse/FI-5870
        // todo don't use TotalDataCount in future
        this.setTotalItemsCountNew(dataSet.length ? Number.MAX_SAFE_INTEGER : this.getLoadedDataCount());

        /*setTimeout(() => {
            // https://chainformation.atlassian.net/browse/FI-5840
            if (!dataSet.length) {
                this.TotalDataCount = this.getLoadedDataCount();
            }
        })*/
    }

    getChildrenDataItems(parentItem: T): Array<T> {
        return this.ChildProperty ? parentItem[this.ChildProperty] || [] : [];
    }

    getItemsWithChildrenRecursively(items: T[]): T[] {
        const res = [];
        items.forEach((item) => {
            this.getItemsWithChildrenRecursively(this.getChildrenDataItems(item)).forEach((childItem) => {
                res.push(childItem);
            });
            res.push(item);
        });

        return res;
    }

    getParentItem(childItem) {
        if (this.parentChildMap.hasOwnProperty(this.getKeyForParentChildMapping(childItem)))
            return this.parentChildMap[this.getKeyForParentChildMapping(childItem)];

        return undefined;
    }

    clearData(hardReset: boolean = false) {
        if (hardReset) {
            this.Data = [];
            this.SelectedItems = [];
        } else {
            this.Data.length = 0;
            this.SelectedItems.length = 0;
        }
        //this.itemsMap = {};
        this.parentChildMap = {};
        if (this.Statefull) {
            const ccmListWrapperElement: Element = Array.from(document.getElementsByClassName('CcmListWrapper'))[0];
            if (ccmListWrapperElement) ccmListWrapperElement.scrollTop = 0;
        }

        this.updateFilterState();
        this.raiseSelectedItemsChanged();
        this.dataCleared$.next();
    }

    // note: not used ?
    getRowClass(data) {}

    getFilterParams() {
        return {};
    }

    getLoadedDataCount() {
        return this.Data.length;
    }

    isLoadMoreAvailable() {
        return this.getLoadedDataCount() < this.TotalDataCount && !this.LoadingMore$.value;
    }

    getOffsetParams() {
        // offset from state received, should load data from beginning
        const offset = this._initialOffset ? 0 : this.getLoadedDataCount();
        const numberOfRecordsToTake = this._initialOffset
            ? this._initialOffset + FiAppSettings.NumberOfRecordsPerPage
            : FiAppSettings.NumberOfRecordsPerPage;

        return {
            Offset: offset,
            NumberOfRecordsToTake: numberOfRecordsToTake,
        };
    }

    getStateForFilter() {
        const stateForFilter = this.getStateForWatch();
        stateForFilter.offset = this.getLoadedDataCount();
        stateForFilter.sortColumn = this.Sort.Column;
        stateForFilter.sortDirection = this.Sort.Reverse;
        return stateForFilter;
    }

    getSortParams() {
        return {
            SelectedSort: {
                ColumnName: this.Sort.Column,
                SortDirection: this.Sort.Reverse,
            },
        };
    }

    getStateForWatch() {
        const stateForWatch: IStateForFilter = {};
        if (isDefined(this.FilterType))
            Object.assign(stateForWatch, {
                filterType: this.FilterType,
            });

        if (this.selectedTags) {
            stateForWatch.selectedTags = this.selectedTags.length ? this.selectedTags.join('|') : null;
        }

        return stateForWatch;
    }

    updateFilterState() {
        if (!this.Statefull) return;

        this.stateService.go(this.stateService.current.name, this.getStateForFilter());
    }

    onScroll(firstVisibleElemIdx) {
        if (!this.Statefull || this.stateService.params.offset == firstVisibleElemIdx) return;

        if (this.stateService.params.offset && !this.Data.length)
            // List was reloaded, dont change state in this place
            return;

        this.stateService.params.offset = firstVisibleElemIdx;
        this.stateService.go(this.stateService.current.name, this.stateService.params, { location: 'replace' });
    }

    deleteClick(isPermanently = false) {
        this.onDeleteClick.emit(isPermanently);
    }

    doDelete(isPermanently = false): any {
        return true;
    }

    IsCheckboxDisabled(data) {
        return false;
    }

    initializeImage(item: T, mediaBankPhysicalFileName: string, mediaBankExtension?: string) {
        if (mediaBankPhysicalFileName) {
            (<any>item).Image = this.getImageUrlInternal(
                item,
                mediaBankPhysicalFileName,
                mediaBankExtension,
                FiImageSize.Thumbnail,
            );
            (<any>item).ImageLarge = this.getImageUrlInternal(
                item,
                mediaBankPhysicalFileName,
                mediaBankExtension,
                FiImageSize.Small,
            );
        }
    }

    private getImageUrlInternal(
        item: T,
        mediaBankPhysicalFileName: string,
        mediaBankExtension: string,
        imageSize?: FiImageSize,
    ) {
        if (!mediaBankPhysicalFileName) return undefined;

        if (!mediaBankExtension) mediaBankExtension = FiAppSettings.DefaultMediaBankImageExtension;

        const changedOn = (() => {
            const c = (<any>item).ChangedOn;
            if (!c) return undefined;
            if (c instanceof Date) return c.toISOString();
            return c;
        })();
        return this.injector
            .get(FiMediaStorageService)
            .getMediaFileUrlByFileName(
                mediaBankPhysicalFileName + mediaBankExtension,
                imageSize || FiImageSize.Thumbnail,
                changedOn,
            );
    }

    //obsolete, todo use getImageUrlInternal with passing extension
    protected getImageUrl(item: T, mediaBankPhysicalFileName: string, imageSize?: FiImageSize): string {
        if (!mediaBankPhysicalFileName) return undefined;
        return this.getImageUrlInternal(item, mediaBankPhysicalFileName, undefined, imageSize);
    }

    isDeleteDisabled() {
        return false;
    }

    toggleChildrenItems(data) {
        const i = this.collapsedParents.indexOf(data);
        if (i > -1) {
            this.collapsedParents.splice(i, 1);
            return;
        }
        this.collapsedParents.push(data);
    }

    protected addDescriptionHeader(isSortable: boolean = true) {
        this.addSpanPlainHeader(this.appTextService.get('common.description'), 'Description', '', isSortable);
    }

    protected addSpanPlainHeader(
        label: string,
        propertyName: string,
        cl: string = '',
        isSortable: boolean = true,
        displayPropertyName?: (data, propertyName: string) => string,
    ) {
        this.Header.push(this.getSpanPlainHeader(label, propertyName, cl, isSortable, displayPropertyName));
    }

    public getSpanPlainHeader(
        label: string,
        propertyName: string,
        cl: string = '',
        isSortable: boolean,
        displayPropertyName: (data, propertyName: string) => string,
    ) {
        return new ItemsListHeader(
            label,
            propertyName,
            new ItemsListHeaderProperties(cl, isSortable, ItemsListElementType.SpanPlain),
            undefined,
            undefined,
            displayPropertyName,
        );
    }

    protected addChangedOnHeader(isSortable: boolean = true) {
        this.Header.push(
            new ItemsListHeader(
                this.appTextService.get('common.last_edited'),
                'ChangedOn',
                new ItemsListHeaderProperties('Ccm-Date', isSortable, ItemsListElementType.SpanChildA),
                'ChangedByUserName',
                {
                    Sref: undefined,
                    SrefParams: () => undefined,
                    onClick: ($event, data) => {
                        $event.preventDefault;
                        setTimeout(() => this.userService.openUserViewDialog(data.ChangedByUserId));
                    },
                },
                (data, propertyName) => {
                    if (typeof data[propertyName] == typeof '') {
                        if (data[propertyName].indexOf('T') == 10)
                            return this.fiFormatDateService.formatDate(
                                new Date(data[propertyName]),
                                FiDateFormatOptions,
                            );

                        return data[propertyName];
                    }
                    return this.fiFormatDateService.formatDate(data[propertyName], FiDateFormatOptions);
                },
            ),
        );
    }

    protected addStartDateHeader(isSortable: boolean = true) {
        this.Header.push(
            new ItemsListHeader(
                this.appTextService.get('common.published'),
                'PublicationStartDate',
                new ItemsListHeaderProperties('Ccm-Date', isSortable, ItemsListElementType.SpanChildA),
                'PublicationSettingChangedByUserName',
                {
                    Sref: undefined,
                    SrefParams: () => undefined,
                    onClick: ($event, data: IPublicationItemBase) => {
                        $event.preventDefault;
                        setTimeout(() => this.userService.openUserViewDialog(data.PublicationSettingChangedByUserId));
                    },
                },
                (data: IPublicationItemBase) => {
                    if (!data.PublicationStartDate) return '';
                    return this.fiFormatDateService.formatDate(data.PublicationStartDate, FiDateFormatOptions);
                },
            ),
        );
    }

    protected addCreatedByHeader() {
        this.Header.push(
            new ItemsListHeader(
                this.appTextService.get('common.created_by'),
                'CreatedByUserName',
                new ItemsListHeaderProperties('BrandedColorHover', true, ItemsListElementType.SpanPlain, {
                    onClick: (event$, data) => this.userService.openUserViewDialog(data.CreatedByUserId),
                }),
            ),
        );
    }

    //#region v2 local search
    isV2SearchEnabled: boolean = false;
    isV2SearchByContent: boolean = false;
    localSearchV2Parameter: ILocalSearchParameter;
    private _lightWeightObjectCollection$ = new ReplaySubject<ILightWeightObjectCollection>(1);
    get lightWeightObjectCollection$(): Observable<ILightWeightObjectCollection> {
        return this._lightWeightObjectCollection$.asObservable();
    }
    raiseLightWeightObjectCollectionChanged(lightWeightObjectCollection: ILightWeightObjectCollection) {
        this._lightWeightObjectCollection$.next(lightWeightObjectCollection);
    }

    private _searchV2Changed$ = new ReplaySubject<ILocalSearchParameter>();
    get searchV2Changed$() {
        return this._searchV2Changed$.asObservable();
    }
    onSearchV2Changed($event: ILocalSearchParameter) {
        this._searchV2Changed$.next($event);
        this.SearchFunction();
    }

    mergeLocalSearchV2Parameter(sourceParameter: ILocalSearchParameter) {
        if (this.localSearchV2Parameter) {
            // copy paste from FiRemoteDataSource, but we should replace all old items lists with new material ones
            sourceParameter.Names = this.localSearchV2Parameter.Names;
            sourceParameter.CreatedByUserNames = this.localSearchV2Parameter.CreatedByUserNames;
            sourceParameter.CreatedByUserIds = this.localSearchV2Parameter.CreatedByUserIds;
            sourceParameter.TagIds = (sourceParameter.TagIds || []).concat(this.localSearchV2Parameter.TagIds || []); // special case, TagIds can be initialized earlier
            sourceParameter.BusinessAreaIds = this.localSearchV2Parameter.BusinessAreaIds;
            sourceParameter.RoleIds = this.localSearchV2Parameter.RoleIds;
            sourceParameter.BusinessUnitIds = this.localSearchV2Parameter.BusinessUnitIds;
            sourceParameter.AreaIds = this.localSearchV2Parameter.AreaIds;
            sourceParameter.ClusterIds = this.localSearchV2Parameter.ClusterIds;
            sourceParameter.Contacts = this.localSearchV2Parameter.Contacts;
            sourceParameter.Addresses = this.localSearchV2Parameter.Addresses;
            sourceParameter.ChecklistNames = this.localSearchV2Parameter.ChecklistNames;
            sourceParameter.MediaBankFileExtensions = this.localSearchV2Parameter.MediaBankFileExtensions;
            sourceParameter.ContentSearchTexts = this.localSearchV2Parameter.ContentSearchTexts;
            sourceParameter.ObjectIds = this.localSearchV2Parameter.ObjectIds;
            sourceParameter.ObjectTypes = this.localSearchV2Parameter.ObjectTypes;
            sourceParameter.DataFieldValues = this.localSearchV2Parameter.DataFieldValues;
        }
    }

    setHighlightTextParam(highlightTextArray: string[]) {
        const nameHeader = this.Header.find((header) => header.Label === this.appTextService.get('common.name'));
        if (nameHeader)
            nameHeader.Properties.ExtraProperties.SrefParams = (data) => {
                return {
                    Id: data.Id,
                    highlightText: highlightTextArray.join(','),
                };
            };
    }
    //#endregion
}
